import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parse } from "date-fns";

import Dashboard from "../../Components/Dashboard";
import Denied from "../../Components/Denied";
import { apiLabs } from "../../Services/api";
import history from "../../Services/history";
import { store } from "../../store";

import goldCup from "../../Assets/img/gold-cup.png";
import silverCup from "../../Assets/img/silver-cup.png";
import bronzeCup from "../../Assets/img/bronze-cup.png";

import {
  globalCSS,
  page,
  dash,
  header,
  kpis,
  podium,
  tableSection,
  summary,
  hourly,
  colors,
} from "./style";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────
const LOJA_KEY = 1;
const PAGE_SIZE = 1000;
const START_HOUR = "06:00";
const API_DATE_FORMAT = "dd/MM/yyyy HH:mm";
const AUTO_REFRESH_MS = 5 * 60 * 1000;

const MEDALS = { 1: goldCup, 2: silverCup, 3: bronzeCup };
const PLACE_LABELS = { 1: "1º LUGAR", 2: "2º LUGAR", 3: "3º LUGAR" };

// ─────────────────────────────────────────────────────────────────────────────
// Formatadores
// ─────────────────────────────────────────────────────────────────────────────
const fmtCurrency = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const MASKED_CURRENCY = "R$ ••••••";
const maskCurrency = () => MASKED_CURRENCY;

// ─────────────────────────────────────────────────────────────────────────────
// Ícones (inline SVG)
// ─────────────────────────────────────────────────────────────────────────────
function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

const fmtNumber = (n) => Number(n || 0).toLocaleString("pt-BR");

const fmtDecimal = (n, digits = 2) =>
  Number(n || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

function parseSaleHour(str) {
  if (!str) return null;
  const parsed = parse(str, "dd/MM/yyyy HH:mm", new Date());
  return isNaN(parsed) ? null : parsed;
}

// ─────────────────────────────────────────────────────────────────────────────
// Agregação
// ─────────────────────────────────────────────────────────────────────────────
function buildOperatorMap(operadores) {
  const map = new Map();
  for (const op of operadores) {
    if (op?.numeroOperador == null) continue;
    map.set(op.numeroOperador, op);
  }
  return map;
}

function aggregate(sales, operatorMap) {
  const byOperator = new Map();
  let totalValor = 0;
  let totalCupons = 0;
  let totalCancelados = 0;
  let totalItens = 0;
  let valorCancelado = 0;
  // Estatísticas por hora — chave "HH:00", valor { hora, cupons, itens, valor }.
  // Usado para o painel de "Vendas por hora" (tabela + gráfico de tendência).
  const hourlyMap = new Map();
  let primeiraVenda = null;
  let ultimaVenda = null;

  for (const venda of sales) {
    const numero = venda.numeroOperador;
    if (numero == null) continue;

    // Cria entry sob demanda na primeira vez que o operador aparece em venda.
    // Tenta pegar o nome no operatorMap (API /loja/operadores); se não houver,
    // usa fallback "Operador #N".
    if (!byOperator.has(numero)) {
      const op = operatorMap.get(numero);
      byOperator.set(numero, {
        numeroOperador: numero,
        nome:
          op?.nomeAbreviado ||
          op?.nomeRazao ||
          `Operador ${numero}`,
        ativo: op?.ativo ?? null,
        cupons: 0,
        cancelados: 0,
        valor: 0,
        valorCancelado: 0,
        itens: 0,
        primeira: null,
        ultima: null,
      });
    }
    const acc = byOperator.get(numero);

    const itens = Array.isArray(venda.itens)
      ? venda.itens.reduce(
          (sum, item) => sum + Number(item.quantidadeVendida || 0),
          0
        )
      : 0;

    if (venda.cancelado) {
      acc.cancelados += 1;
      acc.valorCancelado += Number(venda.valorTotal || 0);
      totalCancelados += 1;
      valorCancelado += Number(venda.valorTotal || 0);
      continue;
    }

    acc.cupons += 1;
    acc.valor += Number(venda.valorTotal || 0);
    acc.itens += itens;

    totalCupons += 1;
    totalValor += Number(venda.valorTotal || 0);
    totalItens += itens;

    const horaDate = parseSaleHour(venda.hora);
    if (horaDate) {
      const horaKey = format(horaDate, "HH:00");
      if (!hourlyMap.has(horaKey)) {
        hourlyMap.set(horaKey, {
          hora: horaKey,
          cupons: 0,
          itens: 0,
          valor: 0,
        });
      }
      const hh = hourlyMap.get(horaKey);
      hh.cupons += 1;
      hh.itens += itens;
      hh.valor += Number(venda.valorTotal || 0);

      if (!primeiraVenda || horaDate < primeiraVenda) primeiraVenda = horaDate;
      if (!ultimaVenda || horaDate > ultimaVenda) ultimaVenda = horaDate;
      if (!acc.primeira || horaDate < acc.primeira) acc.primeira = horaDate;
      if (!acc.ultima || horaDate > acc.ultima) acc.ultima = horaDate;
    }
  }

  // Lista ordenada por hora (ascendente) com itens arredondados pra exibição.
  const hourlyStats = Array.from(hourlyMap.values())
    .map((h) => ({ ...h, itens: Math.round(h.itens) }))
    .sort((a, b) => a.hora.localeCompare(b.hora));

  const ranking = Array.from(byOperator.values())
    .map((o) => ({
      ...o,
      // Razões (ticketMedio e itensPorCupom) usam a soma BRUTA dos itens para
      // não perder precisão. O `itens` exibido é arredondado depois, pois alguns
      // produtos têm quantidadeVendida fracionária (vendas por peso — ex.: 0,250 kg).
      ticketMedio: o.cupons > 0 ? o.valor / o.cupons : 0,
      itensPorCupom: o.cupons > 0 ? o.itens / o.cupons : 0,
      itens: Math.round(o.itens),
    }))
    // Ordena pela quantidade de itens registrados — o que mede o esforço real
    // da operadora no caixa. Em empate, desempata pelo valor total.
    .sort((a, b) => b.itens - a.itens || b.valor - a.valor);

  // Pico = hora com mais ITENS registrados (não cupons). Itens refletem o
  // esforço real do caixa naquele intervalo.
  let picoHora = null;
  let picoQtd = 0;
  for (const h of hourlyStats) {
    if (h.itens > picoQtd) {
      picoQtd = h.itens;
      picoHora = h.hora;
    }
  }

  return {
    ranking,
    hourlyStats,
    totals: {
      valor: totalValor,
      cupons: totalCupons,
      cancelados: totalCancelados,
      valorCancelado,
      // Total geral também arredondado para exibição consistente; razão usa
      // o total bruto para preservar precisão.
      itens: Math.round(totalItens),
      ticketMedio: totalCupons > 0 ? totalValor / totalCupons : 0,
      itensPorCupom: totalCupons > 0 ? totalItens / totalCupons : 0,
      operadoresAtivos: ranking.filter((o) => o.cupons > 0).length,
      primeiraVenda,
      ultimaVenda,
      picoHora,
      picoQtd,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Componentes
// ─────────────────────────────────────────────────────────────────────────────
function StatusPill({ loading, lastUpdate, onClick }) {
  const label = loading
    ? "ATUALIZANDO..."
    : lastUpdate
    ? `ATUALIZADO ${format(lastUpdate, "HH:mm")}`
    : "AGUARDANDO DADOS";
  return (
    <div
      onClick={loading ? undefined : onClick}
      style={header.pill(loading)}
      title={loading ? "Atualizando..." : "Clique para atualizar agora"}
    >
      <span style={header.pillDot(loading)} />
      <span>{label}</span>
    </div>
  );
}

function KpiCard({ idx, label, value, unit, hint }) {
  return (
    <div style={kpis.card(idx)}>
      <div style={kpis.label}>{label}</div>
      <div style={kpis.value}>
        {value}
        {unit && <span style={kpis.unit}>{unit}</span>}
      </div>
      {hint && <div style={kpis.hint}>{hint}</div>}
    </div>
  );
}

function PodiumSpot({ place, operator, hideMoney }) {
  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;
  if (!operator) {
    return (
      <div style={podium.spot(place)}>
        <img src={MEDALS[place]} alt="" style={podium.medal(place)} />
        <div style={podium.placeTag(place)}>{PLACE_LABELS[place]}</div>
        <div style={{ ...podium.name(place), color: colors.textSoft }}>
          Sem dados
        </div>
      </div>
    );
  }
  return (
    <div style={podium.spot(place)}>
      <img src={MEDALS[place]} alt="" style={podium.medal(place)} />
      <div style={podium.placeTag(place)}>{PLACE_LABELS[place]}</div>
      <div style={podium.name(place)}>{operator.nome}</div>
      <div style={podium.operator}>OP #{operator.numeroOperador}</div>
      <div style={podium.metrics}>
        <div style={podium.metricRow}>
          <span style={podium.metricLabel}>Itens</span>
          <span style={podium.metricValue}>{fmtNumber(operator.itens)}</span>
        </div>
        <div style={podium.metricRow}>
          <span style={podium.metricLabel}>Valor</span>
          <span style={podium.metricValue}>{fmtMoney(operator.valor)}</span>
        </div>
        <div style={podium.metricRow}>
          <span style={podium.metricLabel}>Ticket</span>
          <span style={podium.metricValue}>
            {fmtMoney(operator.ticketMedio)}
          </span>
        </div>
      </div>
    </div>
  );
}

function PodiumPanel({ ranking, hideMoney }) {
  const hasAnySales = ranking.some((o) => o.cupons > 0);
  return (
    <section style={podium.wrap}>
      <div style={podium.head}>
        <h2 style={podium.title}>PÓDIO DE PERFORMANCE</h2>
        <span style={podium.subtitle}>
          Top 3 operadores por quantidade de itens registrados
        </span>
      </div>
      {!hasAnySales ? (
        <div style={podium.empty}>
          Nenhuma venda encontrada para o período selecionado.
        </div>
      ) : (
        <div style={podium.grid}>
          <PodiumSpot place={2} operator={ranking[1]} hideMoney={hideMoney} />
          <PodiumSpot place={1} operator={ranking[0]} hideMoney={hideMoney} />
          <PodiumSpot place={3} operator={ranking[2]} hideMoney={hideMoney} />
        </div>
      )}
    </section>
  );
}

function RankingTable({ ranking, hideMoney }) {
  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;
  const ativos = ranking.filter((o) => o.cupons > 0 || o.cancelados > 0).length;

  return (
    <section style={tableSection.wrap}>
      <div style={tableSection.head}>
        <h2 style={tableSection.title}>RANKING COMPLETO</h2>
        <span style={tableSection.count}>
          {fmtNumber(ranking.length)} operador(es) ·{" "}
          {fmtNumber(ativos)} com movimentação
        </span>
      </div>
      <div style={tableSection.scroll}>
        {ranking.length === 0 ? (
          <div style={tableSection.empty}>Nenhum operador encontrado.</div>
        ) : (
          <table style={tableSection.table}>
            <thead>
              <tr>
                <th style={tableSection.th}>#</th>
                <th style={tableSection.th}>Operador</th>
                <th style={tableSection.th}>Nº</th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Cupons
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Itens
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Itens / Cupom
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Ticket Médio
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Cancelados
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Valor Total
                </th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((op, i) => {
                const place = i + 1;
                return (
                  <tr key={op.numeroOperador} style={tableSection.tr(i)}>
                    <td style={tableSection.td}>
                      <span style={tableSection.rank(place)}>{place}º</span>
                    </td>
                    <td
                      style={{
                        ...tableSection.td,
                        whiteSpace: "normal",
                        fontWeight: 600,
                      }}
                    >
                      {op.nome}
                    </td>
                    <td
                      style={{
                        ...tableSection.td,
                        fontFamily: "'Geist Mono', monospace",
                        color: colors.textSoft,
                      }}
                    >
                      {op.numeroOperador}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtNumber(op.cupons)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtNumber(op.itens)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtDecimal(op.itensPorCupom, 1)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtMoney(op.ticketMedio)}
                    </td>
                    <td
                      style={{
                        ...tableSection.td,
                        ...tableSection.tdRight,
                        color: op.cancelados > 0 ? colors.bad : colors.textSoft,
                      }}
                    >
                      {fmtNumber(op.cancelados)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtMoney(op.valor)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Painel "Vendas por hora" — chart de tendência (SVG) + tabela
// ─────────────────────────────────────────────────────────────────────────────
function HourlyTrendChart({ data, metric, peakHora }) {
  // Gráfico de área SVG: 1 ponto por hora, com linha smooth + área preenchida.
  // viewBox fixo + preserveAspectRatio="none" → o SVG estica horizontalmente
  // pra preencher o container, mantendo a altura.
  const W = 1000;
  const H = 220;
  const PAD = { top: 24, right: 14, bottom: 30, left: 14 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  if (data.length === 0) {
    return (
      <div style={hourly.empty}>
        Nenhuma venda registrada até o momento.
      </div>
    );
  }

  const values = data.map((d) => d[metric]);
  const maxVal = Math.max(...values, 1);
  const denom = Math.max(1, data.length - 1);
  const points = data.map((d, i) => ({
    x: PAD.left + (chartW * i) / denom,
    y: PAD.top + chartH - (d[metric] / maxVal) * chartH,
    ...d,
  }));

  // Path com curva Bezier suave entre os pontos (Catmull-Rom-ish simplificado).
  const linePath = points.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x},${p.y}`;
    const prev = points[i - 1];
    const midX = (prev.x + p.x) / 2;
    return acc + ` C ${midX},${prev.y} ${midX},${p.y} ${p.x},${p.y}`;
  }, "");

  const baselineY = PAD.top + chartH;
  const last = points[points.length - 1];
  const first = points[0];
  const areaPath = `${linePath} L ${last.x},${baselineY} L ${first.x},${baselineY} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: 240, display: "block" }}
    >
      <defs>
        <linearGradient id="hourly-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.brand} stopOpacity="0.32" />
          <stop offset="100%" stopColor={colors.brand} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Linhas de grade horizontais */}
      {[0.25, 0.5, 0.75, 1].map((t) => {
        const y = PAD.top + chartH * (1 - t);
        return (
          <line
            key={t}
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y}
            y2={y}
            stroke={colors.border}
            strokeDasharray="2 4"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}

      {/* Baseline */}
      <line
        x1={PAD.left}
        x2={W - PAD.right}
        y1={baselineY}
        y2={baselineY}
        stroke={colors.borderStrong}
        vectorEffect="non-scaling-stroke"
      />

      {/* Área preenchida */}
      <path d={areaPath} fill="url(#hourly-area)" />

      {/* Linha de tendência */}
      <path
        d={linePath}
        fill="none"
        stroke={colors.brand}
        strokeWidth="2.5"
        vectorEffect="non-scaling-stroke"
      />

      {/* Pontos + destaque do pico */}
      {points.map((p) => {
        const isPeak = p.hora === peakHora;
        return (
          <g key={p.hora}>
            <circle
              cx={p.x}
              cy={p.y}
              r={isPeak ? 7 : 4.5}
              fill={isPeak ? colors.brand : colors.surface}
              stroke={colors.brand}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
            {isPeak && (
              <text
                x={p.x}
                y={p.y - 14}
                textAnchor="middle"
                fontSize="13"
                fontWeight="800"
                fill={colors.brand}
                fontFamily={"'Geist', sans-serif"}
              >
                ★ pico
              </text>
            )}
          </g>
        );
      })}

      {/* Labels das horas */}
      {points.map((p) => (
        <text
          key={`l-${p.hora}`}
          x={p.x}
          y={H - 8}
          textAnchor="middle"
          fontSize="12"
          fill={colors.textSoft}
          fontFamily={"'Geist Mono', monospace"}
        >
          {p.hora}
        </text>
      ))}
    </svg>
  );
}

function HourlyPanel({ hourlyStats, totals, hideMoney }) {
  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;
  const [metric, setMetric] = useState("itens"); // "itens" | "valor"

  return (
    <section style={hourly.wrap}>
      <div style={hourly.head}>
        <div>
          <h2 style={hourly.title}>VENDAS POR HORA</h2>
          <div style={{ ...hourly.subtitle, marginTop: 4 }}>
            Volume hora a hora — visualize os horários mais quentes até o momento
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {totals.picoHora && (
            <span style={hourly.peakBadge}>
              ★ PICO {totals.picoHora} ·{" "}
              {fmtNumber(totals.picoQtd)} itens
            </span>
          )}
          <div style={hourly.toggle}>
            <button
              type="button"
              onClick={() => setMetric("itens")}
              style={hourly.toggleBtn(metric === "itens")}
            >
              Itens
            </button>
            <button
              type="button"
              onClick={() => setMetric("valor")}
              style={hourly.toggleBtn(metric === "valor")}
            >
              Valor
            </button>
          </div>
        </div>
      </div>

      <div style={hourly.chartWrap}>
        <HourlyTrendChart
          data={hourlyStats}
          metric={metric}
          peakHora={totals.picoHora}
        />
      </div>

      {hourlyStats.length > 0 && (
        <table style={hourly.table}>
          <thead>
            <tr>
              <th style={hourly.th}>Hora</th>
              <th style={{ ...hourly.th, ...hourly.thRight }}>Cupons</th>
              <th style={{ ...hourly.th, ...hourly.thRight }}>Itens</th>
              <th style={{ ...hourly.th, ...hourly.thRight }}>Valor Total</th>
            </tr>
          </thead>
          <tbody>
            {hourlyStats.map((h, i) => {
              const isPeak = h.hora === totals.picoHora;
              return (
                <tr key={h.hora} style={hourly.tr(i, isPeak)}>
                  <td style={{ ...hourly.td, ...hourly.tdHora }}>
                    {h.hora}
                    {isPeak && <span style={hourly.peakChip}>PICO</span>}
                  </td>
                  <td style={{ ...hourly.td, ...hourly.tdRight }}>
                    {fmtNumber(h.cupons)}
                  </td>
                  <td style={{ ...hourly.td, ...hourly.tdRight }}>
                    {fmtNumber(h.itens)}
                  </td>
                  <td style={{ ...hourly.td, ...hourly.tdRight }}>
                    {fmtMoney(h.valor)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function SummaryPanel({ totals, ranking, hideMoney }) {
  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;
  const top = ranking.find((o) => o.cupons > 0);
  const items = [
    {
      label: "Operador campeão",
      value: top
        ? `${top.nome} · ${fmtNumber(top.itens)} itens`
        : "Sem dados",
    },
    {
      label: "Pico de vendas",
      value: totals.picoHora
        ? `${totals.picoHora} · ${fmtNumber(totals.picoQtd)} itens`
        : "—",
    },
    {
      label: "Janela de operação",
      value:
        totals.primeiraVenda && totals.ultimaVenda
          ? `${format(totals.primeiraVenda, "HH:mm")} → ${format(
              totals.ultimaVenda,
              "HH:mm"
            )}`
          : "—",
    },
    {
      label: "Cupons cancelados",
      value:
        totals.cancelados > 0
          ? `${fmtNumber(totals.cancelados)} · ${fmtMoney(
              totals.valorCancelado
            )}`
          : "Nenhum",
    },
    {
      label: "Itens por cupom (média)",
      value: fmtDecimal(totals.itensPorCupom, 1),
    },
    {
      label: "Operadores ativos",
      value: `${fmtNumber(totals.operadoresAtivos)} no período`,
    },
  ];

  return (
    <section style={summary.wrap}>
      <h3 style={summary.title}>DESTAQUES PARA TOMADA DE DECISÃO</h3>
      <div style={summary.list}>
        {items.map((it) => (
          <div key={it.label} style={summary.item}>
            <div style={summary.itemLabel}>{it.label}</div>
            <div style={summary.itemValue}>{it.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────────────────────
function RankingCashier(props) {
  const {
    location: { idPage },
  } = props;

  const [auth, setAuth] = useState(null);
  const [operadores, setOperadores] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [pagesFetched, setPagesFetched] = useState(0);
  const [hideMoney, setHideMoney] = useState(false);

  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;

  async function verifyAuth() {
    const { profile } = store.getState().user;
    const res = await apiLabs.post("valid_key", {
      id: profile.id,
      menu_id: idPage,
    });
    if (res.data.message === "User not authorized") return setAuth("denied");
    if (res.data.message === "User authorized") return setAuth("authorized");
    return history.push({ pathname: "/", state: { from: props.location } });
  }

  const fetchOperadores = useCallback(async () => {
    try {
      const res = await apiLabs.get("/loja/operadores", {
        params: {
          lojaKey: LOJA_KEY,
          status: true,
          comDadosDeCadastro: true,
          pageSize: PAGE_SIZE,
        },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setOperadores(data);
    } catch (err) {
      console.warn("RankingCashier — erro ao buscar operadores:", err);
      setOperadores([]);
    }
  }, []);

  const fetchTodaySales = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPagesFetched(0);

    try {
      const today = new Date();
      const dataHoraInicial = format(
        new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          Number(START_HOUR.split(":")[0]),
          Number(START_HOUR.split(":")[1])
        ),
        API_DATE_FORMAT
      );

      const all = [];
      let currentPage = 0;
      let pages = 0;

      // Loop com guarda contra travamento (máx 50 páginas = 50k cupons)
      while (pages < 50) {
        const url =
          `/loja/ranking?dataHoraInicial=${encodeURIComponent(dataHoraInicial)}` +
          `&lojaKey=${LOJA_KEY}` +
          `&pageSize=${PAGE_SIZE}` +
          `&currentPage=${currentPage}`;

        const res = await apiLabs.get(url);
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        all.push(...data);
        pages += 1;
        setPagesFetched(pages);

        if (data.length < PAGE_SIZE) break;
        currentPage += 1;
      }

      setSales(all);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("RankingCashier — erro ao buscar vendas:", err);
      setError(
        "Erro ao carregar as vendas. Verifique a conexão e tente novamente."
      );
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth !== "authorized") return;
    fetchOperadores();
  }, [auth, fetchOperadores]);

  // Carga inicial + auto-refresh a cada 5min
  useEffect(() => {
    if (auth !== "authorized") return;

    fetchTodaySales();
    const id = setInterval(fetchTodaySales, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [auth, fetchTodaySales]);

  const operatorMap = useMemo(() => buildOperatorMap(operadores), [operadores]);
  const { ranking, totals, hourlyStats } = useMemo(
    () => aggregate(sales, operatorMap),
    [sales, operatorMap]
  );

  function handleRefresh() {
    if (loading) return;
    fetchTodaySales();
  }

  const operadoresAtivosCadastrados = operadores.filter((o) => o.ativo).length;
  const todayLabel = format(new Date(), "dd/MM/yyyy");

  return (
    <Dashboard title="Ranking Caixas">
      {auth === "denied" && <Denied />}
      {auth === "authorized" && (
        <>
          <style>{globalCSS}</style>
          <div className="ranking-cashier" style={page}>
            <div style={dash}>
              {/* HEADER */}
              <header style={header.wrap}>
                <div>
                  <h1 style={header.title}>RANKING DE CAIXAS</h1>
                  <p style={header.subtitle}>
                    Performance dos operadores · Loja {LOJA_KEY} · Hoje, {todayLabel} a partir das {START_HOUR} · Atualiza a cada 5 min
                  </p>
                </div>
                <div style={header.right}>
                  <button
                    type="button"
                    onClick={() => setHideMoney((v) => !v)}
                    style={header.iconButton(hideMoney)}
                    title={
                      hideMoney
                        ? "Mostrar valores monetários"
                        : "Ocultar valores monetários"
                    }
                    aria-label={
                      hideMoney
                        ? "Mostrar valores monetários"
                        : "Ocultar valores monetários"
                    }
                  >
                    {hideMoney ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                  <StatusPill
                    loading={loading}
                    lastUpdate={lastUpdate}
                    onClick={handleRefresh}
                  />
                </div>
              </header>

              {/* ERRO */}
              {error && (
                <div
                  style={{
                    background: colors.badSoft,
                    border: `1px solid ${colors.bad}`,
                    borderRadius: 12,
                    padding: "12px 18px",
                    color: colors.bad,
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  ⚠ {error}
                  {pagesFetched > 0 && (
                    <>
                      {" · "}
                      {fmtNumber(pagesFetched)} página(s) carregada(s) antes da
                      falha
                    </>
                  )}
                </div>
              )}

              {/* KPIs */}
              <section style={kpis.grid}>
                <KpiCard
                  idx={0}
                  label="Valor total"
                  value={fmtMoney(totals.valor)}
                  hint={`${fmtNumber(totals.cupons)} cupons válidos`}
                />
                <KpiCard
                  idx={1}
                  label="Ticket médio"
                  value={fmtMoney(totals.ticketMedio)}
                  hint={`${fmtDecimal(totals.itensPorCupom, 1)} itens por cupom`}
                />
                <KpiCard
                  idx={2}
                  label="Itens vendidos"
                  value={fmtNumber(totals.itens)}
                  hint="Total de unidades nos cupons"
                />
                <KpiCard
                  idx={3}
                  label="Operadores ativos"
                  value={fmtNumber(totals.operadoresAtivos)}
                  hint={`${fmtNumber(operadoresAtivosCadastrados)} cadastrados ativos`}
                />
                <KpiCard
                  idx={4}
                  label="Cupons cancelados"
                  value={fmtNumber(totals.cancelados)}
                  hint={
                    totals.cancelados > 0
                      ? fmtMoney(totals.valorCancelado)
                      : "Sem cancelamentos"
                  }
                />
                <KpiCard
                  idx={5}
                  label="Pico de vendas"
                  value={totals.picoHora || "—"}
                  unit={totals.picoHora ? "h" : ""}
                  hint={
                    totals.picoQtd > 0
                      ? `${fmtNumber(totals.picoQtd)} itens na hora`
                      : "Sem registros"
                  }
                />
              </section>

              {/* PÓDIO */}
              <PodiumPanel ranking={ranking} hideMoney={hideMoney} />

              {/* VENDAS POR HORA — tabela + gráfico de tendência */}
              <HourlyPanel
                hourlyStats={hourlyStats}
                totals={totals}
                hideMoney={hideMoney}
              />

              {/* RANKING COMPLETO */}
              <RankingTable ranking={ranking} hideMoney={hideMoney} />

              {/* DESTAQUES */}
              <SummaryPanel totals={totals} ranking={ranking} hideMoney={hideMoney} />
            </div>
          </div>
        </>
      )}
    </Dashboard>
  );
}

export default RankingCashier;
